import {
  Loader2,
  Save,
  AlertCircle,
  Rocket,
  RefreshCw,
  Code,
  MessageSquarePlus,
  GitBranch,
  ChevronDown,
  Trash2,
  GitMerge,
} from "lucide-react";
import { WeaveIcon } from "./WeaveIcon";
import { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import type { EditorToolbarProps } from "../types";

/**
 * EditorToolbar — Presentational toolbar for the code editor.
 *
 * All buttons are optional: they only render when their callback is provided.
 * All state is passed in via props — no internal stores or API calls.
 */
export function EditorToolbar({
  isDirty = false,
  isSaving = false,
  isSyncing = false,
  isEdited = false,
  editedFilesCount = 0,
  isDeploying = false,
  deploymentMessage,
  isFormatting = false,
  isAIPanelOpen = false,
  hasAnyActivityRunning = false,
  // Branch
  branchName,
  viewingMain = false,
  isMerging = false,
  isAbandoning = false,
  onSave,
  onSync,
  onFormat,
  onDeploy,
  onOpenAIPanel,
  onLoadIntoChat,
  onMerge,
  onAbandon,
  onToggleViewMain,
  // GitHub
  viewingGithub = false,
  isLoadingGithub = false,
  onToggleViewGithub,
}: EditorToolbarProps) {
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toLowerCase().includes("mac");
  const saveShortcut = isMac ? "⌘S" : "Ctrl+S";
  const formatShortcut = isMac ? "⇧⌥F" : "Shift+Alt+F";

  const hasBranch = !!branchName;
  const isBusy = isMerging || isAbandoning || isSaving || isDeploying || isFormatting || isSyncing || hasAnyActivityRunning;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  return (
    <div className="h-10 border-b border-border flex items-center px-3 justify-between bg-muted/20 gap-2">

      {/* ── Left: branch pill + status indicators + sync ── */}
      <div className="flex items-center gap-2 min-w-0">

        {/* Source dropdown — visible whenever GitHub is available or file has local edits */}
        {(onToggleViewGithub || (hasBranch && onToggleViewMain)) && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              disabled={isBusy}
              title="Switch view"
              className={cn(
                "inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors",
                "focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed",
                viewingGithub
                  ? "bg-violet-500/10 border-violet-500/20 text-violet-400"
                  : viewingMain
                    ? "bg-sky-500/10 border-sky-500/20 text-sky-400"
                    : hasBranch
                      ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
                      : "bg-sky-500/10 border-sky-500/20 text-sky-400",
              )}
            >
              <GitBranch className="h-3 w-3 shrink-0" />
              <span className="font-medium">
                {viewingGithub ? "GitHub" : viewingMain ? "Salesforce" : hasBranch ? "Local" : "Salesforce"}
              </span>
              {isLoadingGithub
                ? <span className="h-3 w-3 animate-spin border border-current border-t-transparent rounded-full" />
                : <ChevronDown className="h-3 w-3" />}
            </button>

            {dropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 min-w-[130px] rounded-md border border-border bg-popover shadow-md py-1">

                {/* Salesforce — always shown */}
                <button
                  onClick={() => { if (viewingGithub) onToggleViewGithub?.(); if (onToggleViewMain && !viewingMain) onToggleViewMain(); setDropdownOpen(false); }}
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-xs transition-colors",
                    viewingMain && !viewingGithub
                      ? "text-sky-400 font-medium bg-sky-500/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                >
                  Salesforce
                </button>

                {/* Local — only when the file has local edits (branch exists) */}
                {hasBranch && onToggleViewMain && (
                  <button
                    onClick={() => { if (viewingMain || viewingGithub) { if (viewingGithub) onToggleViewGithub?.(); else onToggleViewMain(); } setDropdownOpen(false); }}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-xs transition-colors",
                      !viewingMain && !viewingGithub
                        ? "text-blue-400 font-medium bg-blue-500/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    )}
                  >
                    Local
                  </button>
                )}

                {/* GitHub — only shown when a handler is provided (i.e. GitHub is connected) */}
                {onToggleViewGithub && (
                  <button
                    onClick={() => { onToggleViewGithub(); setDropdownOpen(false); }}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2",
                      viewingGithub
                        ? "text-violet-400 font-medium bg-violet-500/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    )}
                  >
                    GitHub
                    {isLoadingGithub && <span className="h-2.5 w-2.5 animate-spin border border-current border-t-transparent rounded-full ml-auto" />}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Separator */}
        {hasBranch && onToggleViewMain && <div className="h-4 w-px bg-border shrink-0" />}

        {/* File status */}
        {isDirty && !viewingMain && !(viewingGithub && hasBranch) && (
          <span className="text-xs text-amber-500 flex items-center gap-1 shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Unsaved
          </span>
        )}
        {(viewingMain || (viewingGithub && hasBranch)) && (
          <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0 italic">
            Read-only preview
          </span>
        )}
        {isEdited && !isDirty && !viewingMain && !(viewingGithub && hasBranch) && (
          <span className="text-xs text-blue-400 flex items-center gap-1 shrink-0">
            <AlertCircle className="h-3 w-3" />
            Ready to deploy
          </span>
        )}

        {/* Sync button — disabled when viewing local branch */}
        {onSync && (
          <button
            onClick={onSync}
            disabled={isSyncing || isDirty || isBusy || (hasBranch && !viewingMain)}
            title={
              hasBranch && !viewingMain
                ? "Switch to Salesforce view to sync"
                : isDirty
                ? "Save changes before syncing"
                : "Sync file from Salesforce"
            }
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded transition-colors shrink-0",
              "text-muted-foreground hover:text-foreground hover:bg-muted",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              isSyncing && "text-primary",
            )}
          >
            <RefreshCw className={cn("h-3 w-3", isSyncing && "animate-spin")} />
            <span>{isSyncing ? "Syncing..." : "Sync"}</span>
          </button>
        )}
      </div>

      {/* ── Right: tools + branch actions + save ── */}
      <div className="flex items-center gap-1.5 shrink-0">

        {/* Format */}
        {onFormat && (
          <button
            onClick={onFormat}
            disabled={isFormatting || isBusy}
            title={`Format Code (${formatShortcut})`}
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded transition-colors",
              "text-muted-foreground hover:text-foreground hover:bg-muted",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              isFormatting && "text-primary",
            )}
          >
            {isFormatting
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Code className="h-3 w-3" />}
            <span>{isFormatting ? "Formatting..." : "Format"}</span>
          </button>
        )}

        {/* Load into chat */}
        {onLoadIntoChat && (
          <button
            type="button"
            onClick={onLoadIntoChat}
            title="Load file into chat"
            className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <MessageSquarePlus className="h-3 w-3" />
            Chat
          </button>
        )}

        {/* Improve with AI */}
        {onOpenAIPanel && !isAIPanelOpen && (
          <button
            onClick={onOpenAIPanel}
            title="Improve code with AI"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-sm hover:shadow-md"
          >
            <WeaveIcon className="h-3.5 w-3.5" />
            Let's Weave
          </button>
        )}

        {/* ── Branch actions separator ── */}
        {hasBranch && (
          <div className="h-4 w-px bg-border mx-0.5" />
        )}

        {/* Discard branch — icon only */}
        {onAbandon && hasBranch && !viewingMain && (
          <button
            onClick={onAbandon}
            disabled={isBusy}
            title="Discard branch changes"
            className={cn(
              "inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
              "text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/20",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {isAbandoning
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        )}

        {/* Merge & Deploy — shown when branch is active */}
        {onMerge && hasBranch && !viewingMain && (
          <button
            onClick={onMerge}
            disabled={isBusy}
            title="Merge branch to main and deploy"
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
              isMerging
                ? "bg-emerald-600/80 text-white cursor-wait"
                : "bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-sm hover:shadow-md",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {isMerging
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Merging...</>
              : <><GitMerge className="h-3.5 w-3.5" />Merge &amp; Deploy</>}
          </button>
        )}

        {/* Deploy — shown when no branch */}
        {onDeploy && !hasBranch && (editedFilesCount > 0 || isDeploying) && (
          <button
            onClick={onDeploy}
            disabled={isDeploying || editedFilesCount === 0}
            title={isDeploying ? deploymentMessage : `Deploy ${editedFilesCount} file${editedFilesCount > 1 ? "s" : ""}`}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
              isDeploying
                ? "bg-blue-600/80 text-white cursor-wait"
                : "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {isDeploying ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /><span className="truncate max-w-[120px]">{deploymentMessage || "Deploying..."}</span></>
            ) : (
              <><Rocket className="h-3.5 w-3.5" />Deploy<span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold bg-white/20 rounded-full">{editedFilesCount}</span></>
            )}
          </button>
        )}

        {/* Save */}
        {onSave && !viewingMain && (
          <button
            onClick={onSave}
            disabled={!isDirty || isSaving || isBusy}
            title={`Save (${saveShortcut})`}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
              isDirty && !isSaving
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {isSaving ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving...</>
            ) : (
              <><Save className="h-3.5 w-3.5" />Save</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
